import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
  parseLicensesPageFromResponse,
  parseSubscriptionFromResponse,
  subscriptionsApi,
  type CreateSubscriptionPayload,
  type SubscriptionLicense,
} from 'src/api/subscriptionsApi';
import { Button } from 'src/common';
import { useUserProfile } from 'src/contexts/UserProfileContext';
import { SubscriptionLicensesTable } from './SubscriptionLicensesTable';
import type { SubscriptionCreateDraft, SubscriptionCycle } from './types';

const formatDate = (date: Date) => {
  const day = String(date.getDate()).padStart(2, '0');
  const month = date.toLocaleString('en-US', { month: 'short' });
  const year = date.getFullYear();
  return `${day} ${month}, ${year}`;
};

const getMonthEndDate = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth() + 1, 0);

const getNextMonthStartDate = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth() + 1, 1);

const getExpectedExpiryDate = (start: Date, cycle: SubscriptionCycle) => {
  if (cycle === 'yearly') {
    const end = new Date(start);
    end.setFullYear(end.getFullYear() + 1);
    return end;
  }
  return getMonthEndDate(start);
};

const getRenewalDate = (start: Date, cycle: SubscriptionCycle) => {
  if (cycle === 'yearly') return getExpectedExpiryDate(start, 'yearly');
  return getNextMonthStartDate(start);
};

interface SubscriptionFieldPreviewProps {
  draft: SubscriptionCreateDraft;
  onBack: () => void;
  onSaved: () => void;
}

export const SubscriptionFieldPreview = ({
  draft,
  onBack,
  onSaved,
}: SubscriptionFieldPreviewProps) => {
  const theme = useTheme();
  const { userData } = useUserProfile();
  const [isSaving, setIsSaving] = useState(false);
  const [createdLicenses, setCreatedLicenses] = useState<SubscriptionLicense[] | null>(
    null,
  );
  const [createdSubscriptionId, setCreatedSubscriptionId] = useState<string | null>(null);
  const [createdLicensesTotal, setCreatedLicensesTotal] = useState(0);
  const [createdLicensesPage, setCreatedLicensesPage] = useState(0);
  const [createdLicensesPageSize, setCreatedLicensesPageSize] = useState(25);
  const [createdLicensesLoading, setCreatedLicensesLoading] = useState(false);
  const startDate = useMemo(() => new Date(), []);

  const createdByName = useMemo(() => {
    const first = userData?.firstName?.trim() || '';
    const last = userData?.lastName?.trim() || '';
    const full = `${first} ${last}`.trim();
    return full || userData?.email || '—';
  }, [userData]);

  const productLabel =
    draft.items.map((item) => item.name).filter(Boolean).join(', ') || '—';
  const licensesLabel = String(
    draft.items.reduce((sum, item) => sum + (Number(item.licenses) || 0), 0),
  );

  const customerReference = draft.customerReference.trim();
  const internalNotes = draft.internalNotes.trim();

  const rows = [
    ...(customerReference
      ? [{ label: 'Customer Reference', value: customerReference }]
      : []),
    { label: 'Client', value: draft.clientName || '—' },
    { label: 'Subscription Type', value: draft.subscriptionType === 'yearly' ? 'Yearly' : 'Monthly' },
    { label: 'Product', value: productLabel },
    { label: 'Number Of Licenses', value: licensesLabel },
    { label: 'Created By', value: createdByName },
    { label: 'Start Date', value: formatDate(startDate) },
    {
      label: 'Expected Expiry Date',
      value: formatDate(getExpectedExpiryDate(startDate, draft.subscriptionType)),
    },
    {
      label: 'Auto Renewal Date',
      value: formatDate(getRenewalDate(startDate, draft.subscriptionType)),
    },
    ...(internalNotes ? [{ label: 'Internal Notes', value: internalNotes }] : []),
  ];

  const productNames = useMemo(
    () =>
      Object.fromEntries(draft.items.map((item) => [item.productId, item.name])),
    [draft.items],
  );

  const loadLicensesPage = async (page0: number, pageSize: number) => {
    if (!createdSubscriptionId) return;
    setCreatedLicensesLoading(true);
    try {
      const response = await subscriptionsApi.getSubscriptionById(createdSubscriptionId, {
        page: page0 + 1,
        size: pageSize,
      });
      const licensesPage = parseLicensesPageFromResponse(response);
      setCreatedLicenses(licensesPage.items);
      setCreatedLicensesTotal(licensesPage.totalRecords);
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          'Failed to load license keys',
      );
    } finally {
      setCreatedLicensesLoading(false);
    }
  };

  const handleSave = async () => {
    const payload: CreateSubscriptionPayload = {
      customerReference: draft.customerReference.trim() || undefined,
      subscriptionType: draft.subscriptionType === 'yearly' ? 'YEARLY' : 'MONTHLY',
      clientId: draft.clientId,
      startDate: startDate.toISOString(),
      internalNotes: draft.internalNotes.trim() || undefined,
      products: draft.items.map((item) => ({
        entityType: item.entityType,
        productId: item.productId,
        numberOfLicenses: Number(item.licenses),
      })),
    };

    setIsSaving(true);
    try {
      const response = await subscriptionsApi.createSubscription(payload);
      toast.success('Subscription created');
      const record = parseSubscriptionFromResponse(response);
      const licensesPage = parseLicensesPageFromResponse(response);
      setCreatedSubscriptionId(record?.id ? String(record.id) : null);
      setCreatedLicenses(licensesPage.items);
      setCreatedLicensesTotal(licensesPage.totalRecords);
      setCreatedLicensesPage(0);
      setCreatedLicensesPageSize(licensesPage.pageSize || 25);
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        'Failed to create subscription';
      toast.error(message);
    } finally {
      setIsSaving(false);
    }
  };

  if (createdLicenses !== null) {
    return (
      <Box>
        <Typography
          style={{
            fontSize: 16,
            fontWeight: 700,
            marginBottom: 8,
            color: theme.palette.text.primary,
          }}>
          License keys
        </Typography>
        <Typography
          style={{
            fontSize: 13,
            color: theme.palette.text.secondary,
            marginBottom: 16,
          }}>
          Copy these keys now. They are also stored on the subscription.
        </Typography>
        <SubscriptionLicensesTable
          licenses={createdLicenses}
          productNames={productNames}
          page={createdLicensesPage}
          pageSize={createdLicensesPageSize}
          totalRecords={createdLicensesTotal}
          loading={createdLicensesLoading}
          onPageChange={async (nextPage) => {
            setCreatedLicensesPage(nextPage);
            await loadLicensesPage(nextPage, createdLicensesPageSize);
          }}
          onPageSizeChange={async (size) => {
            setCreatedLicensesPageSize(size);
            setCreatedLicensesPage(0);
            await loadLicensesPage(0, size);
          }}
        />
        <Box display="flex" justifyContent="flex-end" style={{ marginTop: 24 }}>
          <Button
            type="button"
            variant="contained"
            color="primary"
            onClick={onSaved}
            sx={{ minWidth: 120, height: 42, fontWeight: 700 }}>
            Done
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <Typography
        style={{
          fontSize: 16,
          fontWeight: 700,
          marginBottom: 16,
          color: theme.palette.text.primary,
        }}>
        Subscription Preview
      </Typography>
      <Box
        style={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 8,
          padding: 20,
          backgroundColor: theme.palette.background.paper,
        }}>
        {rows.map((row) => (
          <Box
            key={row.label}
            display="flex"
            justifyContent="space-between"
            style={{ gap: 12, marginBottom: 12 }}>
            <Typography
              style={{
                fontSize: 13,
                color: theme.palette.text.secondary,
                flexShrink: 0,
              }}>
              {row.label}
            </Typography>
            <Typography
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: theme.palette.text.primary,
                textAlign: 'right',
              }}>
              {row.value}
            </Typography>
          </Box>
        ))}
      </Box>

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        style={{ marginTop: 24 }}>
        <Button
          type="button"
          variant="outlined"
          color="inherit"
          disabled={isSaving}
          onClick={onBack}
          sx={{ minWidth: 120, height: 42 }}>
          Back
        </Button>
        <Button
          type="button"
          variant="contained"
          color="primary"
          loading={isSaving}
          disabled={isSaving}
          onClick={handleSave}
          sx={{ minWidth: 120, height: 42, fontWeight: 700 }}>
          Save
        </Button>
      </Box>
    </Box>
  );
};
